/**
 * Online card payment for clients via Stripe.
 *
 * Two stages:
 *  1. Enter an amount → create a PaymentIntent on the backend
 *     (`POST /payments/stripe/intent`) and receive its `clientSecret`.
 *  2. Mount Stripe Elements with that secret and confirm the card payment.
 *
 * The payment is ultimately reconciled server-side by the Stripe webhook, so on
 * success we invalidate cached payment data and let the user know it's settling.
 *
 * Rendered only when `REACT_APP_STRIPE_PUBLISHABLE_KEY` is configured.
 */
import React, { useState } from "react";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateStripeIntent } from "../api/payments";
import { queryKeys } from "../api/queryKeys";
import { env } from "../config/env";
import { Modal } from "./ui/Modal";
import { Button, TextField } from "./ui/Form";
import { toast } from "./ui/Toast";
import { getErrorMessage } from "../lib/errors";
import { formatCurrency } from "../utils/helpers";

let stripePromise: Promise<Stripe | null> | null = null;
function getStripePromise() {
  if (!stripePromise && env.stripePublishableKey) {
    stripePromise = loadStripe(env.stripePublishableKey);
  }
  return stripePromise;
}

export const StripePaymentModal: React.FC<{
  outstanding?: number;
  onClose: () => void;
}> = ({ outstanding, onClose }) => {
  const createIntent = useCreateStripeIntent();
  const [amount, setAmount] = useState(outstanding && outstanding > 0 ? String(outstanding) : "");
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const startPayment = async () => {
    const value = Number(amount);
    if (!value || value <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    try {
      const intent = await createIntent.mutateAsync({ amount: value });
      if (!intent.clientSecret) {
        toast.error("Online payments are not available right now.");
        return;
      }
      setClientSecret(intent.clientSecret);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const promise = getStripePromise();

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Pay Online"
      description="Securely pay by card via Stripe."
      size="md"
      footer={
        clientSecret ? undefined : (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={startPayment} loading={createIntent.isPending}>Continue</Button>
          </>
        )
      }
    >
      {!clientSecret ? (
        <TextField
          label="Amount"
          type="number"
          step="0.01"
          min={1}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
        />
      ) : promise ? (
        <Elements
          stripe={promise}
          options={{ clientSecret, appearance: { theme: "night", labels: "floating" } }}
        >
          <CardForm amount={Number(amount)} onDone={onClose} />
        </Elements>
      ) : (
        <p className="text-sm text-slate-400">Stripe is not configured.</p>
      )}
    </Modal>
  );
};

const CardForm: React.FC<{ amount: number; onDone: () => void }> = ({ amount, onDone }) => {
  const stripe = useStripe();
  const elements = useElements();
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const pay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });
    setSubmitting(false);

    if (error) {
      toast.error(error.message ?? "Payment failed");
      return;
    }
    toast.success("Payment submitted — it will reflect shortly.");
    qc.invalidateQueries({ queryKey: queryKeys.payments.all });
    onDone();
  };

  return (
    <div className="space-y-5">
      <PaymentElement />
      <Button onClick={pay} loading={submitting} disabled={!stripe} className="w-full">
        Pay {formatCurrency(amount)}
      </Button>
    </div>
  );
};
